import type {
  StoreFrontDto,
  StoreFrontOptionsDefaultViewEnum,
} from '@b-stock/listing-api-client'
import type { SavedSearchResponseDto } from '@b-stock/saved-search-api-client'
import {
  BuyingFormatEnum,
  type AllListingsDealsEnum,
  type AllListingsFilters,
} from '@b-stock/search-api-client'

import {
  isDistanceRange,
  type DistanceRange,
} from '@/components/listings/search/SearchFilters/shared/commonTypes'
import { formatConditionValues } from '@/components/listings/search/SearchFilters/shared/filterUtils'
import {
  formatKeysToNames,
  formatNamesToKeys,
} from '@/components/listings/search/SearchFilters/shared/regionFilterUtils'
import {
  validateSortBy,
  validateSortOrder,
} from '@/components/listings/search/sortByDropdown/utils'
import { sanitizeRange } from '@/helpers/utils'
import type { SearchParams } from '@/queries/listingSearchQuery'

import calculateActiveFilterCount from './calculateActiveFilterCount'
import { getAppliedThreshold, isThresholdStep } from './categoryThreshold'
import {
  defaultState,
  getDefaultSort,
  getView,
  LISTING_SEARCH_DEFAULT_PAGE_SIZE,
} from './defaultState'
import formatFilters from './formatFilters'
import {
  isDateRange,
  isRange,
  isStringList,
  isSupportedQueryParam,
} from './typeInspectors'
import {
  BID_STATUS_LOSING,
  BID_STATUS_NO_BID_YET,
  BID_STATUS_WINNING,
  CLOSES_IN_BUCKET_MS,
  isClosesInBucket,
} from './types'
import type {
  BooleanFilterKey,
  ClosesInBucket,
  ListingFiltersState,
  ListingSearchScope,
  ListingSearchState,
  QueryParams,
  SimilarShelfPageState,
  SimilarShelfResolution,
} from './types'

const booleanFilterKeys = new Set(
  Object.entries(defaultState.filters)
    .filter(([, v]) => typeof v === 'boolean')
    .map(([k]) => k as BooleanFilterKey)
)

const VALID_BID_STATUS_VALUES: ReadonlySet<string> = new Set([
  BID_STATUS_WINNING,
  BID_STATUS_LOSING,
  BID_STATUS_NO_BID_YET,
])

export const urlParamsToListingSearchState = ({
  allFilters,
  pageDefaultView,
  queryParams,
  storefront,
}: {
  allFilters?: AllListingsFilters
  pageDefaultView?: StoreFrontOptionsDefaultViewEnum
  queryParams: QueryParams
  storefront?: StoreFrontDto | null
}): ListingSearchState => {
  const { q, p, s, o, id, l, newSeller, landedCostPct } = queryParams

  const filters = Object.entries(queryParams).reduce(
    (acc, [key, value]) => {
      if (!isSupportedQueryParam(key) && key in defaultState.filters) {
        const filterKey = key as keyof ListingFiltersState

        // bidStatus is a plain string param, not JSON — parse it directly.
        // Whitelisted against the known filter values so a crafted URL like
        // `?bidStatus=foobar` can't set an inert filter (which would render
        // the "Clear filters" affordance without any actual filtering).
        if (filterKey === 'bidStatus' && typeof value === 'string' && value) {
          if (VALID_BID_STATUS_VALUES.has(value)) {
            acc.bidStatus = value
          }
          return acc
        }

        // closesIn is a plain string bucket key (?closesIn=5m), not JSON.
        // Whitelisted for the same reason as bidStatus above — an invalid
        // value would render "Clear filters" without any actual filtering.
        if (filterKey === 'closesIn' && typeof value === 'string' && value) {
          if (isClosesInBucket(value)) {
            acc.closesIn = value
          }
          return acc
        }

        if (value && typeof value === 'string') {
          try {
            const parsed = JSON.parse(value) as unknown

            // Handle numeric filters (like msrpPercentage, problemFreeOrderRate)
            if (typeof parsed === 'number' && filterKey === 'msrpPercentage') {
              acc.msrpPercentage = parsed
            } else if (
              typeof parsed === 'number' &&
              filterKey === 'problemFreeOrderRate'
            ) {
              acc.problemFreeOrderRate = parsed
            } else if (
              typeof parsed === 'number' &&
              filterKey === 'categoryThreshold'
            ) {
              // Accept only the 5 stepped values; silently drop anything else
              if (isThresholdStep(parsed)) {
                acc.categoryThreshold = parsed
              }
            } else if (
              typeof parsed === 'boolean' &&
              booleanFilterKeys.has(filterKey as BooleanFilterKey)
            ) {
              ;(acc as Record<string, unknown>)[filterKey] = parsed
            } else if (!(Array.isArray(parsed) || isDistanceRange(parsed))) {
              throw new Error(
                `Expected an array or DistanceRange but got: ${typeof parsed}`
              )
            } else if (filterKey === 'region') {
              acc.region = formatNamesToKeys(
                parsed as ListingFiltersState['region'],
                allFilters?.region || []
              )
              // TODO: If data changes in the future to only send "New", this can be reverted
            } else if (filterKey === 'condition') {
              acc.condition = formatConditionValues(
                parsed as ListingFiltersState['condition'],
                allFilters?.condition || []
              )
            } else if (filterKey === 'currentBidRanges' && isRange(parsed)) {
              acc.currentBidRanges = sanitizeRange(parsed)
            } else {
              ;(acc as Record<string, unknown>)[key] = parsed
            }
          } catch (e) {
            console.warn(`Failed to parse filter value for ${key}:`, e)
          }
        }
      }

      return acc
    },
    { ...defaultState.filters }
  )

  if (typeof q === 'string') {
    filters.q = q
  }

  // landedCostPct is a short URL alias for the landedCostPercentage state key
  if (typeof landedCostPct === 'string') {
    try {
      const parsed = JSON.parse(landedCostPct) as unknown
      if (typeof parsed === 'number') {
        filters.landedCostPercentage = parsed
      }
    } catch (e) {
      console.warn('Failed to parse filter value for landedCostPct:', e)
    }
  }

  if (newSeller === 'true') {
    filters.newSeller = true
  }

  const view = getView({
    pageDefaultView,
    searchParams: queryParams,
    storefront,
  })

  const [defaultSortBy, defaultSortOrder] = getDefaultSort({
    hasQuery: !!q,
    view,
  })

  return {
    page: (() => {
      if (typeof p !== 'string' || p.length > 11) return 1
      const parsed = parseInt(p, 10)
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
    })(),
    sortBy: validateSortBy(s, defaultSortBy),
    sortOrder: validateSortOrder(o, defaultSortOrder),
    view,
    filters,
    appliedFromSavedSearch: false,
    applyingSearchQuery: false,
    // Freeze Sort is never persisted to the URL (GLOB-4832 Req 5).
    isSortFrozen: false,
    id:
      typeof id === 'string'
        ? (JSON.parse(id) as ListingSearchState['id'])
        : null,
    pageSize:
      typeof l === 'string'
        ? parseInt(l)
        : LISTING_SEARCH_DEFAULT_PAGE_SIZE[view],
  }
}

export const unformatFilters = (currentSavedSearch: SavedSearchResponseDto) => {
  const {
    filters: {
      minBidValue,
      maxBidValue,
      minUnits,
      maxUnits,
      minRetailPrice,
      maxRetailPrice,
      distanceLat,
      distanceLong,
      distanceRadius,
      distanceUnit,
      maxPercentMsrp,
      minProblemFreeOrderRate,
      ...restFilters
    },
  } = currentSavedSearch

  const filters: Partial<ListingFiltersState> = {
    ...restFilters,
  }

  // create currentBidRanges
  if (minBidValue !== undefined || maxBidValue !== undefined) {
    filters.currentBidRanges = [minBidValue ?? null, maxBidValue ?? null]
  }

  if (minUnits !== undefined || maxUnits !== undefined) {
    filters.units = [minUnits ?? null, maxUnits ?? null]
  }

  if (minRetailPrice !== undefined || maxRetailPrice !== undefined) {
    filters.msrp = [minRetailPrice ?? null, maxRetailPrice ?? null]
  }

  if (maxPercentMsrp !== undefined) {
    filters.msrpPercentage = maxPercentMsrp
  }

  if (minProblemFreeOrderRate !== undefined) {
    // Preserve two decimal places (the UI input's precision) when converting
    // the stored decimal (0.3456) back to a percentage (34.56)
    filters.problemFreeOrderRate =
      Math.round(minProblemFreeOrderRate * 10000) / 100
  }

  const zipCodePattern = /(\d{5}(-?\d{4})?)$/

  // find the distanceRange display name inside displayFilters
  const distanceString = currentSavedSearch.displayFilters.find((filter) => {
    // ignore user entered values which are always wrapped in double quotes
    if (filter.startsWith('"') && filter.endsWith('"')) return false

    return new RegExp(`\\sfrom\\s${zipCodePattern.source}`).test(filter)
  })

  // create distanceRange
  if (
    distanceString &&
    distanceLat !== undefined &&
    distanceLong !== undefined &&
    distanceRadius !== undefined &&
    distanceUnit !== undefined
  ) {
    const zipCodeMatch = distanceString.match(zipCodePattern)
    const zipCode = zipCodeMatch ? (zipCodeMatch[1] ?? null) : null

    filters.distanceRange = {
      coordinates: {
        lat: distanceLat,
        long: distanceLong,
      },
      distance: distanceRadius,
      zipCode,
    }
  }

  return filters
}

// Translate a closesIn bucket into a fresh [now, now + N] endTime window.
// Kept as a call rather than inlined so `now` is re-evaluated on every
// listingSearchStateToSearchParams invocation (pagination change, filter
// change, refresh) instead of snapshotted at click time.
const resolveClosesInWindow = (
  bucket: ClosesInBucket
): { minEndTime: string; maxEndTime: string } => {
  const now = new Date()
  return {
    minEndTime: now.toISOString(),
    maxEndTime: new Date(
      now.getTime() + CLOSES_IN_BUCKET_MS[bucket]
    ).toISOString(),
  }
}

export const listingSearchStateToSearchParams = (
  { page, sortBy, sortOrder, filters, id, pageSize }: ListingSearchState,
  storefront?: StoreFrontDto,
  scope?: ListingSearchScope
): SearchParams['params'] => {
  const offset = (page - 1) * pageSize

  const formattedFilters = formatFilters(filters)

  // closesIn drives minEndTime/maxEndTime at request time. Applied *after*
  // formatFilters so it overrides any minEndTime/maxEndTime the endTimeRange
  // translator produced from state (a stale bookmarked window would otherwise
  // fight the fresh closesIn window). Never persisted to state or URL.
  if (filters.closesIn) {
    const { minEndTime, maxEndTime } = resolveClosesInWindow(filters.closesIn)
    formattedFilters.minEndTime = minEndTime
    formattedFilters.maxEndTime = maxEndTime
  }

  // No Bid Yet leaves out listings in a Buy Now phase, which can't take a bid.
  // Search reads AUCTION as "not in a Buy Now window" at request time.
  if (filters.bidStatus === BID_STATUS_NO_BID_YET) {
    formattedFilters.buyingFormat = [BuyingFormatEnum.Auction]
  }

  // Safety check: relevance sort requires a search query
  // If state somehow has relevance without query, fall back to endTime
  const safeSortBy =
    sortBy === 'relevance' && !formattedFilters.q ? 'endTime' : sortBy

  return {
    ...formattedFilters,
    // Deals filter is the only array filter using an enum. We keep it as string[] internally
    // for consistency with other filters, and cast to AllListingsDealsEnum[] here at the API boundary.
    deals: formattedFilters.deals as AllListingsDealsEnum[] | undefined,
    limit: pageSize,
    offset,
    sortBy: safeSortBy,
    sortOrder,
    ...(id ? { id: id } : {}),
    ...(storefront
      ? {
          // If we get a storefront filter from a Saved Search (or someone manually adding it
          // to their URL query parameters for some crazy reason), we want to ignore it if
          // we're on the Seller Storefront page
          storefront: undefined,
          storeFrontId: [
            // If storefront is passed in, it should have an id... but just in case it would be
            // safer to show nothing than listings from all sellers if the id was ever missing.
            storefront._id ?? '---',
          ],
        }
      : {}),
    ...scope,
  }
}

type UrlParams = {
  [key: string]: string
}

export const listingSearchStateToUrlParams = ({
  state,
  allFilters,
  pageDefaultView,
  storefront,
  preserveIdInUrl = true,
}: {
  state: ListingSearchState
  allFilters?: AllListingsFilters
  pageDefaultView?: StoreFrontOptionsDefaultViewEnum
  storefront?: StoreFrontDto
  // Pages where the `id` allowlist is a derived, implicit scope (e.g.
  // Watchlist) opt out of writing it to the URL. See useListingStateURLRectification.
  preserveIdInUrl?: boolean
}): UrlParams => {
  const urlParams: UrlParams = {}

  const [defaultSortBy, defaultSortOrder] = getDefaultSort({
    hasQuery: !!state.filters.q,
    view: state.view,
  })

  // Stale relevance (query cleared) resets to the view default
  const [sortBy, sortOrder] =
    !state.filters.q && state.sortBy === 'relevance'
      ? [defaultSortBy, defaultSortOrder]
      : [state.sortBy, state.sortOrder]

  if (sortBy !== defaultSortBy || sortOrder !== defaultSortOrder) {
    urlParams.o = sortOrder
    urlParams.s = sortBy
  }

  Object.entries(state.filters).forEach((filter) => {
    const [key, value] = filter

    if (
      key === 'endTimeRange' &&
      isDateRange(value) &&
      (value[0] || value[1])
    ) {
      // Gated on key, not just isDateRange's shape — isDateRange only checks
      // "array of 2 string-or-null", which any 2-item string[] filter also
      // satisfies (e.g. region: ['CA', 'NY']). Without the key check, a
      // string-list filter with exactly 2 selections would take this branch
      // instead of isStringList below and skip its key-specific formatting
      // (formatKeysToNames for region, formatConditionValues for condition).
      urlParams[key] = JSON.stringify(value)
    } else if (isStringList(value) && value.length > 0) {
      urlParams[key] = JSON.stringify(
        key === 'region'
          ? formatKeysToNames(value, allFilters?.region || [])
          : // TODO: If data changes in the future to only send "New", this can be reverted
            key === 'condition'
            ? formatConditionValues(value, allFilters?.condition || [])
            : value
      )
    } else if (value && isRange(value) && (value[0] || value[1])) {
      urlParams[key] = JSON.stringify(value)
    } else if (key === 'distanceRange') {
      const distanceRange = value as DistanceRange
      if (distanceRange.distance && distanceRange.zipCode) {
        urlParams[key] = JSON.stringify(value)
      }
    } else if (
      typeof value === 'number' &&
      key !== 'landedCostPercentage' &&
      key !== 'categoryThreshold'
    ) {
      // Handle numeric filters like msrpPercentage.
      // landedCostPercentage is handled separately below with a short URL key.
      // categoryThreshold is handled separately below with a category guard.
      urlParams[key] = JSON.stringify(value)
    } else if (
      booleanFilterKeys.has(key as BooleanFilterKey) &&
      value === true
    ) {
      urlParams[key] = 'true'
    }
  })

  if (state.filters.q) {
    urlParams.q = state.filters.q
  }

  // bidStatus is a single string ('winning' | 'losing' | 'no_bid_yet') —
  // serialized as a plain URL param, not JSON. Undefined means "All" (no filter).
  if (state.filters.bidStatus) {
    urlParams.bidStatus = state.filters.bidStatus
  }

  // closesIn is a bucket key ('5m' | '30m' | '1h' | '24h') — persist the key,
  // not the resolved timestamps, so refreshes re-evaluate `now`.
  if (state.filters.closesIn) {
    urlParams.closesIn = state.filters.closesIn
  }

  // landedCostPercentage uses a short URL key to keep URLs compact
  if (typeof state.filters.landedCostPercentage === 'number') {
    urlParams.landedCostPct = JSON.stringify(state.filters.landedCostPercentage)
  }

  // categoryThreshold: only serialize when non-null AND at least one category selected.
  const appliedThreshold = getAppliedThreshold(state.filters)
  if (appliedThreshold !== null) {
    urlParams.categoryThreshold = JSON.stringify(appliedThreshold)
  }

  // Add page parameter if not on first page
  if (state.page && state.page !== 1) {
    urlParams.p = state.page.toString()
  }

  // Default view is what you get without query params
  const defaultView = getView({ pageDefaultView, storefront })
  if (state.view !== defaultView) {
    urlParams.view = state.view.toLowerCase()
  }

  // Default page size is for the current view, not the default view
  const defaultPageSize = LISTING_SEARCH_DEFAULT_PAGE_SIZE[state.view]
  if (state.pageSize !== defaultPageSize) {
    urlParams.l = state.pageSize.toString()
  }

  if (state.filters.newSeller === true) {
    urlParams.newSeller = 'true'
  }

  // When myBids or bidStatus drives `id`, it's derived from a live bid-status
  // fetch, not user input — persist only the flag/statuses (handled by the
  // generic loop above) so a bookmarked/shared URL re-derives fresh IDs on
  // load instead of replaying a stale list. Pages where the whole `id` scope
  // is derived (e.g. Watchlist) opt out entirely via preserveIdInUrl=false.
  const hasBidStatusFilter = !!state.filters.bidStatus
  if (
    preserveIdInUrl &&
    state.id &&
    !state.filters.myBids &&
    !hasBidStatusFilter
  ) {
    urlParams.id = JSON.stringify(state.id)
  }

  return urlParams
}

export const hasNonKeywordFilters = (
  filters: ListingFiltersState,
  allFilters: AllListingsFilters | undefined
): boolean => {
  const total = calculateActiveFilterCount(filters, allFilters)
  const qContribution = filters.q ? 1 : 0
  return total - qContribution > 0
}

/**
 * Which similar-inventory placement, if any, this page qualifies for.
 *
 * The two placements are deliberately disjoint — a page with results can only
 * ever be the end-of-list shelf, a page with none can only ever be the
 * zero-results shelf. Nothing satisfies both, so each flag measures its own
 * placement instead of borrowing the other's traffic, and `reason` is never
 * ambiguous.
 *
 * Page state only: the caller ANDs in the LaunchDarkly treatments, because
 * exposure has to be evaluated on eligibility before the treatment is known.
 */
export const resolveSimilarShelf = ({
  isPlaceholderData,
  total,
  isLastPage,
  isGridView,
  hasFilters,
  isMyBidsSurface,
  isSearchSurface,
}: SimilarShelfPageState): SimilarShelfResolution => {
  // Not `isFetching`: that is also true for a same-key background refetch,
  // which would drop a shelf the buyer is already looking at.
  const settled = !isPlaceholderData && total !== undefined
  const isZeroResults =
    settled && total === 0 && !isMyBidsSurface && isSearchSurface
  const isEndOfList =
    isLastPage && !!total && isGridView && hasFilters && isSearchSurface

  return {
    eligibleForEndOfList: isEndOfList,
    eligibleForZeroResults: isZeroResults,
    // `isLastPage` reads `isLoading`, so it survives a refetch onto the old
    // totals — `settled` keeps a buyer heading for zero results out of this arm.
    exposeEndOfList: isEndOfList && settled,
    exposeZeroResults: isZeroResults,
    reason: isZeroResults ? 'zero_results' : 'end_of_served_listings',
  }
}
